import { Dispatch, MutableRefObject, SetStateAction, UIEvent, useRef, useState } from "react";

/**
 * Floor for the un-anchored fallback threshold — keeps it meaningful when the identity block's
 * height can't be measured (e.g. in jsdom, where every layout box is zero).
 */
const FALLBACK_MIN_THRESHOLD_PX = 40;

/**
 * How far the container must be scrolled before the identity block hands over to the condensed
 * strip.
 *
 * With an anchor (the name row), the answer is exact: the strip must take over at the instant the
 * name would otherwise disappear under the top edge, which is when `scrollTop` reaches the name's
 * bottom in the scroller's content coordinates. Not earlier — the real name is still on screen and
 * the strip would cover it — and not later, which is the bug this replaced: the threshold used to be
 * 55% of the *whole* identity stack (person card + action row + company card), several hundred
 * pixels below the point where the name leaves, so the lead's name was simply absent in between —
 * and on a short tab the container ran out of scroll first and the strip could never appear at all
 * (PREC-4116).
 *
 * Without an anchor, fall back to the original "identity block is ~55% scrolled away" heuristic.
 */
export const condenseThresholdPx = ({
	anchorBottomPx,
	identityHeightPx,
}: {
	/** Bottom edge of the name row in the scroller's content coordinates, or `null` if unmeasurable. */
	anchorBottomPx: number | null;
	identityHeightPx: number;
}): number => {
	if (anchorBottomPx === null) {
		return Math.max(FALLBACK_MIN_THRESHOLD_PX, identityHeightPx * 0.55);
	}

	return Math.max(0, anchorBottomPx);
};

/**
 * Where the anchor's bottom edge sits in the scroller's content coordinates — i.e. its distance from
 * the top of the scrollable content, which is what `scrollTop` is measured against.
 *
 * Returns `null` when the anchor isn't laid out (no element, or a zero-height box as in jsdom), so
 * the caller falls back to the height-based threshold rather than trusting a measurement of nothing.
 */
const anchorBottomInContent = (
	container: HTMLElement,
	anchor: HTMLElement | null
): number | null => {
	if (!anchor || anchor.offsetHeight === 0) {
		return null;
	}

	return (
		anchor.getBoundingClientRect().bottom -
		container.getBoundingClientRect().top +
		container.scrollTop
	);
};

/**
 * Drives the "condense the identity block into a slim sticky strip while scrolling" behavior shared
 * by the lead detail surfaces (the Contacts lead panel and the List detail view). Mirrors the v81
 * mockup's `.lp-strip`: the moment the lead's name would scroll off the top, the surface switches to
 * the condensed strip and sticks the tabs row below it.
 *
 * - `scrollRef` goes on the scrolling container.
 * - `anchorRef` goes on the name row — the element the strip stands in for.
 * - `identityRef` goes on the person/company/placement block that scrolls away (fallback measurement
 *   for hosts that don't set an anchor).
 * - `onScroll` wires the container's scroll event.
 * - `setCondensed` lets the caller reset the state (e.g. when the lead changes).
 */
export const useCondenseOnScroll = (): {
	condensed: boolean;
	setCondensed: Dispatch<SetStateAction<boolean>>;
	scrollRef: MutableRefObject<HTMLDivElement | null>;
	identityRef: MutableRefObject<HTMLDivElement | null>;
	anchorRef: MutableRefObject<HTMLDivElement | null>;
	onScroll: (event: UIEvent<HTMLDivElement>) => void;
} => {
	const [condensed, setCondensed] = useState(false);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const identityRef = useRef<HTMLDivElement | null>(null);
	const anchorRef = useRef<HTMLDivElement | null>(null);

	const onScroll = (event: UIEvent<HTMLDivElement>) => {
		const container = event.currentTarget;
		const threshold = condenseThresholdPx({
			anchorBottomPx: anchorBottomInContent(container, anchorRef.current),
			identityHeightPx: identityRef.current?.offsetHeight ?? 0,
		});

		setCondensed(container.scrollTop > threshold);
	};

	return { condensed, setCondensed, scrollRef, identityRef, anchorRef, onScroll };
};
